/**
 * Desktop/tablet checkout geometry — photo-forward order context on the
 * left, payment rail on the right. Phone callers pass `editorial={false}`
 * and get an identity wrap so the mobile stack stays a single-column
 * checkout.
 *
 * `editorial` MUST be `isDesktop || isTablet`. `isDesktop` is large web
 * only; iPad and Android tablets are `isTablet` even past 1024. Gating
 * this row on `isDesktop` alone leaves native tablets as a fat phone.
 *
 * Padding matches AppTopNav (`SPACING.lg`). Do not nest this inside
 * ScreenContainer's 800px `content` column — that is the stretched-phone
 * failure mode.
 */
import { type ReactNode } from 'react'
import { Platform, View, type ViewStyle } from 'react-native'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { SPACING, RADIUS } from '@/lib/brand'

const webStickyRail =
  Platform.OS === 'web'
    ? ({ position: 'sticky' as 'relative', top: SPACING.lg } as const)
    : ({ position: 'relative' as const } as const)

export function CheckoutEditorialRow({
  editorial,
  padX,
  children,
  testID,
}: {
  editorial: boolean
  padX: number
  children: ReactNode
  testID?: string
}) {
  if (!editorial) return <>{children}</>
  return (
    <View
      testID={testID}
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        gap: SPACING.lg,
        paddingHorizontal: padX,
        width: '100%',
      }}
    >
      {children}
    </View>
  )
}

export function CheckoutSummaryColumn({
  editorial,
  children,
}: {
  editorial: boolean
  children: ReactNode
}) {
  if (!editorial) return <>{children}</>
  return (
    <View
      style={{
        flexGrow: 1.4,
        flexShrink: 1,
        flexBasis: 280,
        minWidth: 0,
      }}
    >
      {children}
    </View>
  )
}

export function CheckoutPaymentRail({
  editorial,
  wide = true,
  children,
  footer,
  testID,
}: {
  editorial: boolean
  /** Cap the rail on large web; tablet two-column needs the extra width. */
  wide?: boolean
  children: ReactNode
  /** Pinned under the fields so the pay CTA never sits below the fold. */
  footer?: ReactNode
  testID?: string
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  if (!editorial) {
    return (
      <View testID={testID}>
        {children}
        {footer}
      </View>
    )
  }

  const webRailClip: ViewStyle | null =
    Platform.OS === 'web'
      ? ({
          maxHeight: 'calc(100dvh - 104px)',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateRows: 'minmax(0, 1fr) auto',
        } as unknown as ViewStyle)
      : null
  const webBodyScroll: ViewStyle | null =
    Platform.OS === 'web'
      ? ({ overflow: 'scroll', minHeight: 0, maxHeight: '100%' } as ViewStyle)
      : null

  return (
    <View
      testID={testID}
      style={[
        {
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: wide ? 380 : 320,
          maxWidth: wide ? 520 : undefined,
          minWidth: 0,
          ...webStickyRail,
          alignSelf: 'flex-start',
          backgroundColor: colors.surface,
          borderRadius: RADIUS.xl,
          padding: SPACING.lg,
          borderWidth: 1,
          borderColor: colors.border,
          ...WEB_SHADOWS.md,
        },
        webRailClip,
      ]}
    >
      <View
        style={[
          {
            flexGrow: 1,
            flexShrink: 1,
            minHeight: 0,
          },
          webBodyScroll,
        ]}
      >
        {children}
      </View>
      {footer ? (
        <View
          style={{
            flexGrow: 0,
            flexShrink: 0,
            paddingTop: SPACING.md,
            marginTop: SPACING.xs,
            borderTopWidth: 1,
            borderTopColor: colors.border,
            backgroundColor: colors.surface,
          }}
        >
          {footer}
        </View>
      ) : null}
    </View>
  )
}
